// Physical stock geometry, verified from Avery's official blank-template PDFs
// (vector paths). Inch offsets are required for alignment, not aesthetic.
const row = (top0, pitch, n) => Array.from({ length: n }, (_, i) => +(top0 + pitch * i).toFixed(6));

export const AVERY_CATALOG = {
  "5160": {
    code: "5160", aliases: ["8160"], name: "Address labels 1 x 2-5/8 (3 x 10)",
    sourceUrl: "https://www.avery.com/templates/5160",
    page: { width: 8.5, height: 11 }, width: 2.625, height: 1, cols: 3, rows: 10,
    lefts: [0.187503, 2.937503, 5.687503], tops: row(0.5, 1, 10),
  },
  "5163": {
    code: "5163", aliases: ["8163"], name: "Shipping labels 2 x 4 (2 x 5)",
    sourceUrl: "https://www.avery.com/templates/5163",
    page: { width: 8.5, height: 11 }, width: 4, height: 2, cols: 2, rows: 5,
    lefts: [0.155557, 4.343756], tops: row(0.5, 2, 5),
  },
  "22816": {
    code: "22816", aliases: [], name: "Square labels 2 x 2 (3 x 4)",
    sourceUrl: "https://www.avery.com/templates/22816",
    page: { width: 8.5, height: 11 }, width: 2, height: 2, cols: 3, rows: 4,
    lefts: [0.625, 3.25, 5.875], tops: [0.625, 3.207639, 5.790972, 8.374306],
  },
};

export const SUPPORTED_CODES = ["5160", "8160", "5163", "8163", "22816"];

export function normalizeProductCode(input) {
  const raw = String(input || "").toUpperCase().replace(/AVERY/g, "").replace(/[\s\-_#]/g, "");
  if (!raw) return { code: "", product: null, error: "Choose a label product." };
  for (const p of Object.values(AVERY_CATALOG)) {
    if (p.code === raw || p.aliases.includes(raw)) return { code: raw, product: p, error: "" };
  }
  return { code: raw, product: null, error: `Unknown label product "${input}". Supported: ${SUPPORTED_CODES.join(", ")}.` };
}

export function labelsPerSheet(p) { return p.cols * p.rows; }

// slot is 0-based, row-major
export function slotRect(p, slot) {
  const r = Math.floor(slot / p.cols);
  const c = slot % p.cols;
  return { left: p.lefts[c], top: p.tops[r], width: p.width, height: p.height };
}

export function validateStart(start, p) {
  const n = Number(start);
  const max = labelsPerSheet(p);
  if (!Number.isInteger(n) || n < 1 || n > max) return `Start position must be a whole number from 1 to ${max}.`;
  return "";
}

// Returns array of sheets; each sheet = array of {slot, label}. Offset applies to first sheet only.
export function paginate(labels, p, start = 1) {
  const per = labelsPerSheet(p);
  const offset = Math.max(0, Math.min(per - 1, (Number(start) || 1) - 1));
  const sheets = [];
  let i = 0;
  let slot = offset;
  while (i < labels.length) {
    const sheet = [];
    while (slot < per && i < labels.length) { sheet.push({ slot, label: labels[i++] }); slot++; }
    sheets.push(sheet);
    slot = 0;
  }
  return sheets;
}

export function sheetCount(count, p, start = 1) {
  if (!count) return 0;
  const per = labelsPerSheet(p);
  return Math.ceil(((Number(start) || 1) - 1 + count) / per);
}

const hasText = (v) => typeof v === "string" && v.trim() !== "";

// Uses only whitelisted fields: never names, contact info, notes, or legacy text.
export function formatLabel(label, listType, opts = {}) {
  const sizes = opts.sizes || { pant: true, shirt: true, pj: true };
  const head = [hasText(label.gender) ? label.gender.trim() : "", label.age === null || label.age === undefined ? "" : `Age ${label.age}`]
    .filter(Boolean).join(" ");
  let body = [];
  if (listType === "toys") {
    if (hasText(label.gift_request)) body = [label.gift_request.trim()];
  } else if (listType === "clothing") {
    if (sizes.pant && hasText(label.pant_size)) body.push(`Pants: ${label.pant_size.trim()}`);
    if (sizes.shirt && hasText(label.shirt_size)) body.push(`Shirt: ${label.shirt_size.trim()}`);
    if (sizes.pj && hasText(label.pj_size)) body.push(`PJ: ${label.pj_size.trim()}`);
  }
  const ids = `${label.household_id || ""} / ${label.member_id || ""}`;
  return { head, body, ids, empty: listType !== "giftcards" && body.length === 0 };
}

export function labelIssues(labels, selected, listType, opts) {
  const chosen = labels.filter((l) => selected.has(l.key));
  const empty = chosen.filter((l) => formatLabel(l, listType, opts).empty);
  return { chosen, empty };
}
